import { snakeCase } from 'snake-case'
import { parsePhoneNumber } from 'libphonenumber-js/max'
import {
    addDays,
    addHours,
    addMinutes,
    addMonths,
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    format,
    isAfter,
    isBefore,
    isThisWeek,
    isToday,
    isTomorrow,
    isValid,
    isWithinInterval,
    parseISO,
    subMinutes,
} from 'date-fns'
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz'
import { BookingOption } from '@/types/interfaces/booking_options'
import { BillingType } from '@/app/orders/pharmacy/[pharmacyOrderId]/options/types'
import type { PharmacyOrder } from '@/types/interfaces/pharmacy'

export const getTimezoneAbbreviation = (timezone: string): string => {
    return (
        new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'short',
        })
            .formatToParts(new Date())
            .find((part) => part.type === 'timeZoneName')?.value || ''
    )
}

const STREET_SUFFIX_ABBREVIATIONS: Record<string, string> = {
    avenue: 'Ave',
    boulevard: 'Blvd',
    circle: 'Cir',
    court: 'Ct',
    drive: 'Dr',
    expressway: 'Expy',
    heights: 'Hts',
    highway: 'Hwy',
    junction: 'Jct',
    lane: 'Ln',
    parkway: 'Pkwy',
    place: 'Pl',
    road: 'Rd',
    square: 'Sq',
    street: 'St',
    terrace: 'Ter',
    trail: 'Trl',
    way: 'Way',
}

export function abbreviateStreetSuffix(address: string): string {
    const lowerAddress = address.toLowerCase()

    for (const [full, abbrev] of Object.entries(STREET_SUFFIX_ABBREVIATIONS)) {
        const suffixRegex = new RegExp(`\\b${full}(?:,|$)`, 'i')
        if (suffixRegex.test(lowerAddress)) {
            return address.replace(new RegExp(`\\b${full}(?=,|$)`, 'i'), abbrev)
        }
    }

    return address
}

// TODO: in formatTimeWithTimezone, we only show the TZ if it's different than the stored home address TZ
export function formatDateTimeToDisplay(datetimeStr: string, showMinutes: boolean = true): string {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const datetimeObj = parseISO(datetimeStr)
    const zonedTime = utcToZonedTime(datetimeObj, timeZone)

    const timezoneAbbr = getTimezoneAbbreviation(timeZone)
    const hourFormat = showMinutes ? `h:mma '${timezoneAbbr}'` : `ha '${timezoneAbbr}'`

    let formattedDatetime: string

    if (isToday(zonedTime)) {
        formattedDatetime = formatInTimeZone(datetimeObj, timeZone, `'Today at' ${hourFormat}`)
    } else if (isTomorrow(zonedTime)) {
        formattedDatetime = formatInTimeZone(datetimeObj, timeZone, `'Tomorrow at' ${hourFormat}`)
    } else if (isThisWeek(zonedTime)) {
        formattedDatetime = formatInTimeZone(datetimeObj, timeZone, `EEEE, MMMM d 'at' ${hourFormat}`)
    } else {
        formattedDatetime = formatInTimeZone(datetimeObj, timeZone, `EEEE, MMMM d 'at' ${hourFormat}`)
    }

    // Capitalize first letter of each word, except 'at' and 'am'/'pm', keep timezone abbreviation uppercase
    return formattedDatetime
        .split(' ')
        .map((word, index, arr) => {
            if (word === 'at') {
                return word.toLowerCase()
            }
            if (index === arr.length - 1) {
                return word.toUpperCase() // Keep timezone abbreviation uppercase
            }
            if (index === arr.length - 2 && (word.toLowerCase() === 'am' || word.toLowerCase() === 'pm')) {
                return word.toLowerCase() // Keep am/pm lowercase
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        })
        .join(' ')
}

export interface QuickViewTimeSlot {
    label: string
    times: Array<{ friendlyTime: string; fullTime: string }>
}

// Input format:
// [
//     '2024-02-29T12:00:00Z',
//     '2024-02-29T13:00:00Z',
//     '2024-02-29T14:00:00Z'
// ]
//
// Output format:
// [{
//     label: "today",
//     times: [{friendlyTime: "9:00am", fullTime: "2024-02-29T12:00:00Z"}],
//  },
//     label: "tomorrow"
//     times: [{friendlyTime: "10:00am", fullTime: "2024-02-29T10:00:00Z"}],
//  },
//  {
//     label: "Monday 5/1"
//     times: [{friendlyTime: "5:00pm", fullTime: "2024-02-29T17:00:00Z"}]
//   }
// }]
export function formatQuickviewTimes(bookingOption: BookingOption): Array<QuickViewTimeSlot> {
    const dateTimeStrings = bookingOption.top_3_availability || bookingOption.availability.slice(0, 3)

    const result: { [key: string]: { label: string; times: Array<{ friendlyTime: string; fullTime: string }> } } = {}

    dateTimeStrings.forEach((datetimeStr) => {
        const date = new Date(datetimeStr)
        let label: string
        const timeString = format(date, 'h:mma').toLowerCase()

        if (isToday(date)) {
            label = 'Today'
        } else if (isTomorrow(date)) {
            label = 'Tomorrow'
        } else if (isThisWeek(date)) {
            label = format(date, 'EEEE M/d')
        } else {
            label = format(date, 'MMMM d')
        }

        if (!result[label]) {
            result[label] = { label, times: [] }
        }
        result[label].times.push({
            friendlyTime: timeString,
            fullTime: datetimeStr,
        })
    })

    return Object.values(result)
}

export function formatAvailabilityGroups(data: any, fillGaps: boolean = false, startDate?: string | null): Array<any> {
    let availabilityGroups = data.map((day: any) => {
        return {
            // Vital returns dates as strings with format: YYYY-MM-DD.  This means
            // that the slots are available at that lab location on that date
            // (relative to its timezone)
            date: day.date,
            numSlots: day.slots.length,
        }
    })

    if (fillGaps && availabilityGroups.length > 0) {
        const dataMap = new Map(availabilityGroups.map((item: any) => [item.date, item]))
        const filledData = []
        let currentDate = startDate ? new Date(startDate) : new Date()
        currentDate.setUTCHours(0, 0, 0, 0) // Normalize
        let endDate = new Date(availabilityGroups.at(-1).date)
        endDate.setUTCHours(0, 0, 0, 0)

        while (currentDate <= endDate) {
            // Although currentDate is in UTC, we don't need to adjust timezone
            // since we are simply iterating through date strings with format YYYY-MM-DD
            const dateStr = currentDate.toISOString().split('T')[0]
            if (dataMap.has(dateStr)) {
                filledData.push(dataMap.get(dateStr))
            } else {
                // For days that are not present in the Vital response, insert
                // a placeholder that indicates no appts available on that day.
                filledData.push({ date: dateStr, numSlots: 0 })
            }
            currentDate.setDate(currentDate.getDate() + 1)
        }
        availabilityGroups = filledData
    }

    // When the human readable date is rendered below, it is initialized using
    // a plain YYYY-MM-DD string.  Date treats that as UTC, so we do not need
    // to adjust it for timezone shift.
    const humanDateFmt = new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    })

    // Formats a date as YYYY-MM-DD in the user's timezone.
    const localDateFmt = new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    })

    // Today and tomorrow in the user's local timezone in YYYY-MM-DD format.
    const date = new Date()
    const today = localDateFmt.format(date)
    date.setDate(date.getDate() + 1)
    const tomorrow = localDateFmt.format(date)

    // Add the label and description for each day.
    availabilityGroups.forEach((day: any) => {
        if (day.date === today) {
            day.label = 'Today'
        } else if (day.date === tomorrow) {
            day.label = 'Tomorrow'
        } else {
            day.label = humanDateFmt.format(new Date(day.date))
        }

        if (day.numSlots === 0) {
            day.description = 'No appts'
        } else if (day.numSlots === 1) {
            day.description = '1 appt'
        } else {
            day.description = `${day.numSlots} appts`
        }
    })

    return availabilityGroups
}

export function summarizedQuickviewTimes(bookingOption: BookingOption): string {
    const quickviewTimes = formatQuickviewTimes(bookingOption)
    const firstTime = quickviewTimes[0]
    return `${firstTime.label} at ${firstTime.times[0].friendlyTime}${firstTime.times[1] ? ', ' + firstTime.times[1]?.friendlyTime : ''}${firstTime.times.length > 2 ? ` + ${firstTime.times.length - 2} more` : ''}`
}

export function formatCentsToDollars(cents: number, showCents: boolean = true): string {
    if (cents == 0) {
        return '$0'
    } else {
        const dollars = cents / 100
        const negativeSign = dollars < 0 ? '-' : ''
        const absDollars = Math.abs(dollars)
        return showCents ? `${negativeSign}$${absDollars.toFixed(2)}` : `${negativeSign}$${absDollars.toFixed(0)}`
    }
}

/**
 * Parses a cents amount from API string/number values.
 * Returns null when missing or not a finite number.
 */
export function parseCentsAmount(cents: string | number | null | undefined): number | null {
    if (cents == null || cents === '') return null
    const value = typeof cents === 'number' ? cents : Number(cents)
    return Number.isFinite(value) ? value : null
}

/**
 * Formats optional API cents (string or number) for display.
 * Returns null when the value is missing or not a finite number.
 */
export function formatOptionalCentsToDollars(
    cents: string | number | null | undefined,
    showCents: boolean = true,
): string | null {
    const value = parseCentsAmount(cents)
    if (value == null) return null
    return formatCentsToDollars(value, showCents)
}

export interface HumanizePriceOptions {
    /** Whether to prefix the output with a currency symbol. @default true */
    showCurrencySymbol?: boolean
    /** Whether to show the cents part of the price. @default 'ifNeeded' */
    showCents?: 'always' | 'ifNeeded' | 'never'
    /** How to round the price when showCents is 'never'. @default 'nearest' */
    round?: 'down' | 'up' | 'nearest'
}

const DEFAULT_HUMANIZE_PRICE_OPTIONS: Required<HumanizePriceOptions> = {
    showCurrencySymbol: true,
    showCents: 'ifNeeded',
    round: 'nearest',
}

export function humanizePrice(cents: number, options: HumanizePriceOptions = {}): string {
    const { showCurrencySymbol, showCents, round } = { ...DEFAULT_HUMANIZE_PRICE_OPTIONS, ...options }
    let dollars = cents / 100
    let hasCents = cents % 100 !== 0

    const intlOptions: Intl.NumberFormatOptions = {
        style: showCurrencySymbol ? 'currency' : 'decimal',
        currency: showCurrencySymbol ? 'USD' : undefined,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }

    if (showCents === 'always') {
        intlOptions.minimumFractionDigits = 2
    } else if (showCents === 'ifNeeded') {
        intlOptions.minimumFractionDigits = hasCents ? 2 : 0
    } else if (showCents === 'never') {
        if (round === 'down') {
            dollars = Math.floor(dollars)
        } else if (round === 'up') {
            dollars = Math.ceil(dollars)
        } else {
            dollars = Math.round(dollars)
        }
        intlOptions.maximumFractionDigits = 0
    }

    return new Intl.NumberFormat('en-US', intlOptions).format(dollars)
}

export function formatPhoneNumber(phoneNumber: number | string | null | undefined): string {
    if (phoneNumber == null) return ''
    const digits = phoneNumber.toString().replace(/\D/g, '')
    const tenDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
    if (tenDigits.length === 10) {
        return `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`
    }
    return phoneNumber.toString()
}

/** Formats a US phone number as "(978) ····9011", keeping only the area code and last four digits. */
export function maskPhoneNumber(phoneNumber: number | string | null | undefined): string | null {
    if (phoneNumber == null) return null
    const digits = phoneNumber.toString().replace(/\D/g, '')
    const tenDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
    if (tenDigits.length !== 10) return null
    return `(${tenDigits.slice(0, 3)}) ····${tenDigits.slice(6)}`
}

// Phone input formatting utilities
// Format: (XXX) XXX - XXXX

export const PHONE_REGEX = /^\(\d{3}\) \d{3} - \d{4}$/

export const VALID_US_REGIONS = ['US', 'CA', 'PR', 'VI', 'GU', 'MP', 'AS']

export function formatPhoneInput(digitsOnly: string): string {
    if (digitsOnly.length === 0) return ''
    if (digitsOnly.length <= 3) return '(' + digitsOnly + ') '
    if (digitsOnly.length <= 6) return '(' + digitsOnly.slice(0, 3) + ') ' + digitsOnly.slice(3)
    return '(' + digitsOnly.slice(0, 3) + ') ' + digitsOnly.slice(3, 6) + ' - ' + digitsOnly.slice(6, 10)
}

export function extractPhoneDigits(value: string): string {
    let digitsOnly = value.replace(/[^\d]/g, '')

    // Handle common autocomplete formats (remove leading 1 for 11-digit numbers)
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
        digitsOnly = digitsOnly.substring(1)
    }

    return digitsOnly.substring(0, 10)
}

export function validatePhoneNumber(value: string): string | null {
    if (!PHONE_REGEX.test(value)) {
        return 'Please enter a valid phone number'
    }

    try {
        const parsedNumber = parsePhoneNumber('+1 ' + value, 'US')
        if (!VALID_US_REGIONS.includes(parsedNumber.country || '')) {
            return 'Please enter a valid US, Canada, or US territory phone number'
        }
        return null
    } catch {
        return 'Please enter a valid phone number'
    }
}

export function phoneToDigits(formattedPhone: string): string {
    return formattedPhone.replace(/[^\d]/g, '')
}

export function formatInitialPhoneNumber(phoneNumber: string): string {
    if (PHONE_REGEX.test(phoneNumber)) return phoneNumber
    const digits = extractPhoneDigits(phoneNumber)
    return formatPhoneInput(digits)
}

export function formatDate(timestamp: string, includeYear?: boolean): string {
    const date = new Date(timestamp)
    const currentYear = new Date().getFullYear()
    const dateYear = date.getFullYear()

    // Include year if explicitly requested or if the year is different from current year
    const shouldIncludeYear = !!includeYear || dateYear !== currentYear

    const options: Intl.DateTimeFormatOptions = {
        month: 'short', // This will display short month names (Jan, Feb, Mar, etc.)
        day: 'numeric',
    }

    if (shouldIncludeYear) {
        options.year = 'numeric'
    }

    return date.toLocaleString('en-US', options)
}

/**
 * Full calendar date of an ISO timestamp in UTC (e.g. "September 1, 2026"), for dates stored as UTC midnight that
 * would otherwise show the previous day in US time zones. Returns null when the value is not a date.
 */
export function formatUtcLongDate(isoTimestamp: string): string | null {
    const date = new Date(isoTimestamp)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

/**
 * Compact age of a past timestamp: minutes, then hours, then days through the
 * first week, then the calendar date. Sub-minute ages (and any future timestamp
 * from clock skew) clamp to "1m" so the unit vocabulary holds. Undefined when the
 * timestamp is unparseable.
 */
export function formatTimeAgo(timestamp: string): string | undefined {
    const date = new Date(timestamp)
    if (Number.isNaN(date.getTime())) return undefined

    const now = new Date()
    const minutes = differenceInMinutes(now, date)
    if (minutes < 60) return `${Math.max(1, minutes)}m`

    const hours = differenceInHours(now, date)
    if (hours < 24) return `${hours}h`

    const days = differenceInDays(now, date)
    if (days <= 7) return `${days}d`

    return formatDate(timestamp)
}

/**
 * Safely formats a date string, returning the fallback if the input is
 * null, undefined, empty, or unparseable (avoids RangeError: Invalid time value).
 */
export function safeFormatDate(dateStr: string | null | undefined, formatStr: string, fallback: string = ''): string {
    if (!dateStr) return fallback
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return fallback
    return format(date, formatStr)
}

/** Converts an API date-of-birth string (YYYY-MM-DD) to display format (MM/DD/YYYY). */
export function formatDateOfBirth(dob?: string | null): string | null {
    if (!dob) return null
    const parts = dob.split('-')
    if (parts.length === 3) return `${parts[1]}/${parts[2]}/${parts[0]}`
    return dob
}

/**
 * Timezone-safe date formatter that parses the YYYY-MM-DD portion of an ISO
 * string in local time, avoiding off-by-one day shifts near midnight. Omits
 * the year when it matches the current year unless `includeYear` is true.
 */
export function formatLocalDate(dateStr: string, includeYear?: boolean): string {
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number)
    const date = new Date(year, month - 1, day)
    const currentYear = new Date().getFullYear()

    const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
    }

    if (includeYear || year !== currentYear) {
        options.year = 'numeric'
    }

    return date.toLocaleDateString('en-US', options)
}

export function formatLocalDateCompact(dateStr: string, includeYear?: boolean): string {
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number)
    const date = new Date(year, month - 1, day)

    const options: Intl.DateTimeFormatOptions = {
        month: '2-digit',
        day: '2-digit',
    }

    if (includeYear || year !== new Date().getFullYear()) {
        options.year = '2-digit'
    }

    return date.toLocaleDateString('en-US', options)
}

export interface FormatVisitScheduledTimeOptions {
    /** IANA timezone; defaults to the browser timezone. */
    timeZone?: string | null
    /**
     * date-fns time pattern.
     * - `h:mm a` → "3:00 PM" (visit checkout)
     * - `h:mmaaa` → "3:00pm" (urgent care)
     * @default 'h:mm a'
     */
    timeFormat?: 'h:mm a' | 'h:mmaaa'
    /** Lowercase the time portion (e.g. "3:00 pm"). @default false */
    lowercaseTime?: boolean
    /**
     * Text inserted before the date/time, e.g. "Starts around ".
     * When set, relative day labels (`Today,` / `Tomorrow,`) are omitted.
     * @default ''
     */
    prefix?: string
    /** Use a non-breaking space before the timezone abbreviation. @default false */
    nonBreakingSpaceBeforeTimeZone?: boolean
}

/**
 * Formats an ISO appointment/slot start for checkout summaries.
 *
 * Examples:
 * - "Today, Feb 26 at 3:00 PM CST"
 * - "Tomorrow, Feb 27 at 3:00pm UTC"
 * - "Starts around Mar 20 at 3:00 pm CDT"
 *
 * Relative day labels (`Today,` / `Tomorrow,`) are omitted when `prefix` is set.
 */
export function formatVisitScheduledTime(
    time: string,
    options: FormatVisitScheduledTimeOptions = {},
): string | null {
    const {
        timeZone,
        timeFormat = 'h:mm a',
        lowercaseTime = false,
        prefix = '',
        nonBreakingSpaceBeforeTimeZone = false,
    } = options

    const resolvedTimeZone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
    const dateTimeObj = parseISO(time)
    if (!isValid(dateTimeObj)) return null

    const zonedTime = utcToZonedTime(dateTimeObj, resolvedTimeZone)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    const calendarDate = format(zonedTime, 'MMM d')
    let datePart = calendarDate
    // Skip Today/Tomorrow when a custom prefix is already framing the label.
    if (!prefix) {
        if (format(zonedTime, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) {
            datePart = `Today, ${calendarDate}`
        } else if (format(zonedTime, 'yyyy-MM-dd') === format(tomorrow, 'yyyy-MM-dd')) {
            datePart = `Tomorrow, ${calendarDate}`
        }
    }

    const timePart = lowercaseTime
        ? format(zonedTime, timeFormat).toLowerCase()
        : format(zonedTime, timeFormat)
    const timeZoneAbbr = formatInTimeZone(zonedTime, resolvedTimeZone, 'zzz')
    const spaceBeforeTimeZone = nonBreakingSpaceBeforeTimeZone ? '\u00a0' : ' '

    return `${prefix}${datePart} at ${timePart}${spaceBeforeTimeZone}${timeZoneAbbr}`
}

export function sentenceCaseDateString(input: string | null): string {
    if (!input) {
        return ''
    }

    const words = input.split(' ')

    const firstLowercase = words[0].toLowerCase()
    if (firstLowercase === 'tomorrow' || firstLowercase === 'today') {
        words[0] = words[0].toLowerCase()
    }

    return words.join(' ')
}

export function toProperCase(str: string): string {
    return str
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
}

export function convertKeysToSnakeCase(obj: any): any {
    if (typeof obj !== 'object' || obj === null) {
        return obj
    }

    if (Array.isArray(obj)) {
        return obj.map(convertKeysToSnakeCase)
    }

    return Object.keys(obj).reduce((acc, key) => {
        const snakeCaseKey = snakeCase(key)
        acc[snakeCaseKey] = convertKeysToSnakeCase(obj[key])
        return acc
    }, {} as any)
}

export function toSentenceCase(str: string): string {
    if (str.length === 0) return str
    return str[0].toUpperCase() + str.slice(1).toLowerCase()
}

// Normalize pharmacy instructions for display.
// Handles ALL CAPS input (common from pharmacy systems) and ensures proper
// sentence capitalization, e.g. "TAKE 1 TABLET DAILY. TAKE WITH FOOD."
// becomes "Take 1 tablet daily. Take with food."
export function formatInstructions(text: string): string {
    const trimmed = text.trim()
    if (!trimmed) return ''
    // Downcase ALL-CAPS input so the re-capitalization below starts clean
    const isAllCaps = trimmed === trimmed.toUpperCase() && trimmed !== trimmed.toLowerCase()
    let s = isAllCaps ? trimmed.toLowerCase() : trimmed
    // Sentence starts: after . ! ? + whitespace. Skip periods that are part of dotted
    // pharmacy SIG abbreviations (p.o., b.i.d., q.d., etc.) — those match letter.dot.letter
    // immediately before the period, not a full word end.
    s = s.replace(/(^|(?<![A-Za-z]\.[A-Za-z])[.!?]\s+)([a-z])/g, (_m, prefix, ch) => prefix + ch.toUpperCase())
    // Ensure the very first character is uppercase
    s = s.charAt(0).toUpperCase() + s.slice(1)
    // Normalize trailing punctuation: keep one existing terminator, or add a period
    const trailingPunct = s.match(/[.!?]$/)
    s = s.replace(/[.!?]+$/, '')
    return s + (trailingPunct ? trailingPunct[0] : '.')
}

// Normalize medication names from pharmacy databases for display.
// Input can be lowercase, ALL CAPS, or mixed — e.g. "cephalexin 500 mg capsule",
// "METFORMIN HCL 500 MG TABLET", "triamcinolone acetonide 0.1 % topical ointment"
// Output: "Cephalexin 500mg Capsule", "Metformin Hcl 500mg Tablet", "Triamcinolone Acetonide 0.1% Topical Ointment"
export function formatMedName(name: string): string {
    let s = name
    if (s === s.toUpperCase() && s !== s.toLowerCase()) s = s.toLowerCase()
    // Collapse "500 %" → "500%" (pharmacy systems often pad the percent sign)
    s = s.replace(/(\d+)\s+%/g, '$1%')
    // Collapse "500 mg" → "500mg" for common pharmaceutical units
    s = s.replace(/(\d+)\s+(mg|mcg|ml|g)\b/gi, '$1$2')
    // Add a space after commas that aren't thousands separators (e.g. ",10%" → ", 10%")
    // but preserve groupings like "1,000" where exactly three digits follow.
    s = s.replace(/,(?!\d{3}(?!\d))(?!\s)/g, ', ')
    // Title-case each word ("cephalexin 500mg capsule" → "Cephalexin 500mg Capsule")
    return s.replace(/\b\w/g, (c) => c.toUpperCase())
}

export function isGreaterThanXHours(datetimeStr: string, hours: number): boolean {
    const datetimeObj = parseISO(datetimeStr)

    const timeZone = 'America/Denver'
    const now = utcToZonedTime(new Date(), timeZone)

    const hoursFromNow = addHours(now, hours)
    return isAfter(datetimeObj, hoursFromNow)
}

export function isWithinTimeRange(
    datetimeStr: string,
    timeZone: string,
    beforeMins: number,
    afterMins: number,
): boolean {
    const datetimeObj = parseISO(datetimeStr)
    const now = utcToZonedTime(new Date(), timeZone)
    const minutesBefore = subMinutes(datetimeObj, beforeMins)
    const minutesAfter = addMinutes(datetimeObj, afterMins)
    return isAfter(now, minutesBefore) && isBefore(now, minutesAfter)
}
export function isPastXMinutes(datetimeStr: string, minutes: number): boolean {
    const datetimeObj = parseISO(datetimeStr)
    const timeZone = 'America/Denver'

    const now = utcToZonedTime(new Date(), timeZone)

    const minutesAfter = addMinutes(datetimeObj, minutes)
    const after = isAfter(now, minutesAfter)
    return isAfter(now, minutesAfter)
}

export function capitalizeFirstLetterDowncaseRest(word: string): string {
    if (!word) return word
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
}

export function capitalizeFirstLetter(word: string | null): string {
    if (!word) return ''
    return word.charAt(0).toUpperCase() + word.slice(1)
}

export function removeAfterAt(dateString: string | null): string {
    if (!dateString) {
        return ''
    }

    return dateString.split(' at')[0]
}

export function extractAfterAt(dateString: string | null): string {
    if (!dateString) {
        return ''
    }

    const parts = dateString.split(' at ')
    return parts.length > 1 ? parts[1].trim() : ''
}

export function roundToOneDecimal(num: number): string {
    if (isNaN(num) || num === 0) return ''
    return (Math.round(num * 10) / 10).toFixed(1)
}

export function formatTimeWithTimezone(time: string, homeAddressTimezone: string): string {
    const getBrowserTimezone = (): string => {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
    }

    const formatTime = (date: Date, timezone: string): string => {
        return formatInTimeZone(date, timezone, 'h:mma').toLowerCase()
    }

    const getTimezoneAbbreviation = (timezone: string): string => {
        const date = new Date()
        return (
            new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                timeZoneName: 'short',
            })
                .formatToParts(date)
                .find((part) => part.type === 'timeZoneName')?.value || ''
        )
    }

    const utcDate = new Date(time)
    const browserTimezone = getBrowserTimezone()

    if (browserTimezone === homeAddressTimezone) {
        return formatTime(utcDate, homeAddressTimezone)
    } else {
        const browserTime = formatTime(utcDate, browserTimezone)
        const timezoneAbbr = getTimezoneAbbreviation(browserTimezone).replace(/^(\w)(\w)(\w)$/, '$1$3')
        return `${browserTime} ${timezoneAbbr}`
    }
}

export const relativeDateTimeString = (dateTimeString: string): string | null => {
    if (!dateTimeString) return null

    const sourceDateTime = new Date(dateTimeString)
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const dateTimeInTimezone = new Date(sourceDateTime.toLocaleString('en-US', { timeZone }))
    const timezoneString = Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).format(new Date()).split(' ')[1]

    const today = new Date()
    const tomorrow = addDays(today, 1)
    const oneWeekFromToday = addDays(today, 7)
    const nineMonthsFromToday = addMonths(today, 9)

    if (isToday(dateTimeInTimezone)) {
        return format(dateTimeInTimezone, "'today at' h:mm a ") + timezoneString
    }

    if (isTomorrow(dateTimeInTimezone)) {
        return format(dateTimeInTimezone, "'tomorrow at' h:mm a ") + timezoneString
    }

    if (isWithinInterval(dateTimeInTimezone, { start: addDays(tomorrow, 1), end: oneWeekFromToday })) {
        return format(dateTimeInTimezone, "EE. MMM d 'at' h:mm a ") + timezoneString
    }

    if (dateTimeInTimezone <= nineMonthsFromToday) {
        return format(dateTimeInTimezone, "EE. MMM d 'at' h:mm a ") + timezoneString
    }

    return format(dateTimeInTimezone, "EE. MMM d, yyyy 'at' h:mm a ") + timezoneString
}

export function downcaseFirstLetter(str: string): string {
    if (!str) return str
    return str[0].toLowerCase() + str.slice(1)
}

export function formatResponseTime(
    responseTime: string,
    withTimezone: boolean = false,
    downcaseFirst: boolean = false,
    homeTimeZone?: string | null,
): string {
    // Parse date and prepare useful values
    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const eventDate = new Date(responseTime)
    const now = new Date()
    const sixDaysFromNow = addDays(new Date(), 6)
    const hoursUntilResponse = differenceInHours(eventDate, now)

    // Show timezone if explicitly requested or if browser timezone differs from home timezone
    const showTimezone = withTimezone || (homeTimeZone && homeTimeZone !== browserTimeZone)

    // Format time with custom function that handles on-the-hour times differently
    const timeFormat = showTimezone ? 'h a zzz' : 'h a'
    const formattedTime = formatInTimeZone(eventDate, homeTimeZone || browserTimeZone, timeFormat)

    // Build the complete formatted string based on time difference and date
    let result: string

    if (isToday(eventDate)) {
        result = formattedTime
    } else if (hoursUntilResponse <= 23) {
        // If it's less than 23 hours away but next day, just show time
        result = formattedTime
    } else if (isTomorrow(eventDate)) {
        result = `${formattedTime} tomorrow`
    } else if (!isAfter(eventDate, sixDaysFromNow)) {
        // Within the next week
        const dayName = format(eventDate, 'EEEE')
        result = `${formattedTime} ${dayName}`
    } else {
        // More than a week away
        const dayName = format(eventDate, 'EEEE')
        const dateStr = format(eventDate, 'M/d')
        result = `${formattedTime} ${dayName} (${dateStr})`
    }

    return downcaseFirst ? downcaseFirstLetter(result) : result
}

export function formatPrice(number: number | undefined, fixed: number = 2) {
    if (number === undefined || number === null) return 'N/A'
    const num = parseFloat(number.toString())
    return num.toFixed(fixed)
}

export const strWithOxfordCommaSeparator = (str: string, index: number, numItems: number): string => {
    if (index === 0) return str
    if (numItems === 1) return str
    if (numItems === 2 && index === 1) return ` and ${str}`
    if (numItems > 2 && index === numItems - 1) return `, and ${str}`
    return `, ${str}`
}

export function formatTimeDisplay(timeString: string): string {
    // Remove timezone if present (e.g., "11:00am ET" -> "11:00am")
    const timeWithoutTz = timeString.replace(/\s+[A-Z]{2,3}$/, '')
    // Convert to uppercase AM/PM and ensure proper spacing
    return timeWithoutTz.replace(/([ap])m/i, (match, p1) => ` ${p1.toUpperCase()}M`)
}

// Format the appointment date and time for follow up visits text
export function formatAppointmentDateTime(date: Date) {
    const dateOptions: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    }
    const timeOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }
    const formattedDate = date.toLocaleDateString('en-US', dateOptions)
    const formattedTime = date.toLocaleTimeString('en-US', timeOptions)
    return `${formattedDate} at ${formattedTime}`
}

/**
 * Lab State Utilities
 *
 * Lab states and their meanings:
 * - "init": The only state we can book from
 * - "pending", "ordered", "received_order", "requisition_created": Labs are booked
 * - "partial_results", "completed": We have some or all results back from the lab
 * - "canceled": Lab was canceled
 */

export function canBookLabs(state?: string): boolean {
    return state === 'init'
}

export function areLabsBooked(state?: string): boolean {
    return ['pending', 'ordered', 'received_order', 'requisition_created'].includes(state || '')
}

export function haveLabResults(state?: string): boolean {
    return ['partial_results', 'completed'].includes(state || '')
}

export function areLabsCanceled(state?: string): boolean {
    return state === 'canceled'
}

export function getLabStatusDisplay(state?: string): string {
    if (canBookLabs(state)) return 'Ready to book'
    if (areLabsBooked(state)) return 'Labs booked'
    if (state === 'partial_results') return 'Some results ready'
    if (state === 'completed') return 'Results ready'
    if (areLabsCanceled(state)) return 'Canceled'
    return 'Unknown status'
}

export function getCompactDaysLabel(days: number | undefined) {
    if (days === null || days === undefined) return
    if (days === 0) return 'same-day'
    if (days === 1) return '1-day'
    if (days === 7) return '1-week'
    if (days === 14) return '2-week'
    if (days === 21) return '3-week'
    if (days === 28) return '4-week'

    // For longer periods, use months
    if (days >= 30) {
        const months = Math.round(days / 30)
        return months === 1 ? '1-month' : `${months}-month`
    }

    // For periods between 4 weeks and 1 month, still use days
    if (days < 30) {
        return `${days}-day`
    }

    // Fallback (shouldn't reach here)
    return `${Math.round(days / 7)}-week`
}

/**
 * Checks if the current pathname is part of the Gen AI user flow
 * These pages should use Gen AI styling when user comes from Gen AI
 */
export function isGenAiFlowPage(pathname: string): boolean {
    return (
        pathname.startsWith('/get-started') ||
        pathname.startsWith('/login') ||
        pathname.startsWith('/verification') ||
        pathname.startsWith('/verify-identity')
    )
}

export function billingTypeRequiresPayment(billingType: BillingType, hasClinicalReview: boolean): boolean {
    return (
        billingType === 'gm_card' ||
        (billingType === 'insurance' && hasClinicalReview) ||
        (billingType === 'pharmacy_delegated' && hasClinicalReview)
    )
}

/**
 * The medication price the patient is committing to under a billing method, matching the figure the
 * billing and review screens show. Null when the price is only known at the pharmacy. Excludes the
 * clinical review fee, which is charged separately as `clinical_fee_cents`.
 *
 * @param insurancePricingEnabled - When false, insurance estimates are hidden ("Priced at pharmacy"),
 *   so insurance billing reports null rather than a figure the patient never saw.
 */
export function pharmacyOrderPurchasedPriceCents(
    order: Pick<PharmacyOrder, 'gm_card_price_cents' | 'insurance_estimate_cents' | 'override_price_cents'>,
    billingType: BillingType,
    insurancePricingEnabled: boolean = true,
): number | null {
    if (order.override_price_cents != null) return order.override_price_cents
    if (billingType === 'gm_card') return order.gm_card_price_cents
    if (billingType === 'insurance') {
        return insurancePricingEnabled ? order.insurance_estimate_cents : null
    }
    return null
}
